import back_arrow from "../assets/backarrow.svg";
import { useEffect, useState } from "react";
import ThemeRowContainer from "../components/survey_overview/ThemeRowContainer";
import SubscriptionNeededPopup from "../components/SubscriptionNeededPopup";

const SummaryQuestions = (props) => {
  const cardType = props.cardType;
  const surveyData = props.surveyData;
  const generatingReport = props.generatingReport;
  const origin = props.origin || "chart";
  const questionsData = props.questionsData || null; // optional override
  const teamVariance = props.teamVariance || [];
  const teamMean = props.teamMean || [];
  const subscription = props.subscription;


  // Toggles
  const [tmEnabled, setTmEnabled] = useState(true);
  const [tlEnabled, setTlEnabled] = useState(true);
  const [stEnabled, setStEnabled] = useState(true);
  const [attributableData, setAttributableData] = useState(false);

  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);

  // Participants: store as { id, name }
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);

  useEffect(() => {
    if (!surveyData) return;
    const tm = surveyData.participants?.team_members || [];
    const tl = surveyData.participants?.team_leaders || [];
    const st = surveyData.participants?.stakeholders || [];

    if (tm.length) setTeamMembers(tm.map(x => x.name));
    if (tl.length) setTeamLeaders(tl.map(x => x.name));
    if (st.length) setStakeholders(st.map(x => x.name));
  }, [surveyData]);

  const handleAttributableToggle = () => {
    if (subscription < 1) {
      setShowSubscriptionPopup(true);
      return;
    }

    setAttributableData(!attributableData);
  };

  const handleBackClick = (ev) => {
    if (!ev) return;
    // pass origin back so the parent knows whether to show pillars or top5s
    if (typeof props.back === "function") {
      return props.back.length >= 2
          ? props.back(cardType, origin)   // new signature
          : props.back(cardType);          // back-compat
    }
  };

  const getTitle = () => {
    if (origin === "top5s") {
      // Label as the Top5s group
      const map = [
        "Team Strengths",
        "Team Weaknesses",
        "Team Variance",
        "Stakeholder Variance",
      ];
      return map[cardType] ?? "Summary";
    }
    // Pillar titles (chart origin)
    switch (cardType) {
      case 0: return "Purpose & Motivation";
      case 1: return "External Systems & Processes";
      case 2: return "Relationships";
      case 3: return "Internal Systems & Processes";
      case 4: return "Learning";
      case 5: return "Leadership";
      default: return "Summary";
    }
  };

  const getContainerClassname = () => {
    if (origin === "top5s") return ""; // neutral styling for cross-pillar sets
    switch (cardType) {
      case 0: return "purpose-and-motivation";
      case 1: return "external-systems-and-processes";
      case 2: return "relationships";
      case 3: return "internal-systems-and-processes";
      case 4: return "learning";
      case 5: return "leadership";
      default: return "";
    }
  };

  // ---- scoring helpers ----
  const calculateMeanScore = (tm_scores, tl_scores, st_scores) => {
    let total = 0, count = 0;
    let scores = [];
    if (tmEnabled && tm_scores) scores = scores.concat(tm_scores);
    if (tlEnabled && tl_scores) scores = scores.concat(tl_scores);
    if (stEnabled && st_scores) scores = scores.concat(st_scores);
    for (const s of scores) { count += 1; total += s.score; }
    if (!count) return 0;
    return Math.round(total / count);
  };

  const getFunctionText = (tm_scores, tl_scores, st_scores) => {
    const score = calculateMeanScore(tm_scores, tl_scores, st_scores);
    if (score < 20) return "Low";
    if (score < 40) return "Medium";
    return "High";
  };

  return (
      <>
        <div className="link" onClick={handleBackClick} style={{display: generatingReport ? "none" : null}}>
          <img className={"back-arrow-classname"} src={back_arrow} alt={"Back Arrow"}/>
        </div>
        <div className="summary-title" style={{
          marginLeft: generatingReport ? "30px" : null,
          padding: generatingReport ? 0 : null,
        }}>
          {getTitle()}
        </div>
        <div className={`summary-questions-container ${getContainerClassname()}`} style={{
          padding: generatingReport ? "30px" : null,
          border: generatingReport ? "none" : null,
          margin: generatingReport ? 0 : null
        }}>
          <div className="summary-questions-controls-container">
            <div className="summary-tabular-title-container">
              <div className="summary-tabular-attributable-switch" style={{opacity: generatingReport ? 0 : null}}>
                <div className={`chart-controls-switch ${attributableData ? "active" : "inactive"}`}
                     onClick={handleAttributableToggle} tabIndex="0">
                  <div className="chart-controls-switch-centre"/>
                </div>
                <div className="summary-tabular-attributable-switch-text">Attributable Data</div>
              </div>
            </div>
            <div className="summary-tabular-controls-container">
              <div className="summary-tabular-control-container">
                <div className="summary-tabular-controls-switch" style={{display: generatingReport ? "none" : null}}>
                  <div className={`chart-controls-switch ${tmEnabled ? "active" : "inactive"}`}
                       onClick={() => setTmEnabled(!tmEnabled)}>
                    <div className="chart-controls-switch-centre"/>
                  </div>
                </div>
                <div className="summary-tabular-controls-text-container">
                  <div className="summary-tabular-colour-circle green"/>
                  <div className="summary-tabular-controls-text">Team Members</div>
                </div>
              </div>
              <div className="summary-tabular-control-container">
                <div className="summary-tabular-controls-switch" style={{display: generatingReport ? "none" : null}}>
                  <div className={`chart-controls-switch ${tlEnabled ? "active" : "inactive"}`}
                       onClick={() => setTlEnabled(!tlEnabled)}>
                    <div className="chart-controls-switch-centre"/>
                  </div>
                </div>
                <div className="summary-tabular-controls-text-container">
                  <div className="summary-tabular-colour-circle pink"/>
                  <div className="summary-tabular-controls-text">Team Leader(s)</div>
                </div>
              </div>
              <div className="summary-tabular-control-container">
                <div className="summary-tabular-controls-switch" style={{display: generatingReport ? "none" : null}}>
                  <div className={`chart-controls-switch ${stEnabled ? "active" : "inactive"}`}
                       onClick={() => setStEnabled(!stEnabled)}>
                    <div className="chart-controls-switch-centre"/>
                  </div>
                </div>
                <div className="summary-tabular-controls-text-container">
                  <div className="summary-tabular-colour-circle blue"/>
                  <div className="summary-tabular-controls-text">Stakeholder(s)</div>
                </div>
              </div>
            </div>
          </div>
          <div className="summary-questions-table-container">
            <div className="summary-questions-table-headings">
              <div className="summary-questions-heading-spacer"/>
              <div className="summary-questions-headings-container">
                {tmEnabled && teamMembers.map((tm, idx) => (
                    <div className="summary-questions-participant-heading" key={`tm-${idx}`}>
                      <div className="summary-questions-participant-name">
                        <div className="name-rotate">
                          {attributableData ? tm : `Team Member ${idx + 1}`}
                        </div>
                      </div>
                      <div className="summary-questions-participant-color tm-green"/>
                    </div>
                ))}
                {tlEnabled && teamLeaders.map((tl, idx) => (
                    <div className="summary-questions-participant-heading" key={`tl-${idx}`}>
                      <div className="summary-questions-participant-name">
                        <div className="name-rotate">
                          {attributableData ? tl : `Team Leader ${idx + 1}`}
                        </div>
                      </div>
                      <div className="summary-questions-participant-color tl-pink"/>
                    </div>
                ))}
                {stEnabled && stakeholders.map((st, idx) => (
                    <div className="summary-questions-participant-heading" key={`st-${idx}`}>
                      <div className="summary-questions-participant-name">
                        <div className="name-rotate">
                          {attributableData ? st : `Stakeholder ${idx + 1}`}
                        </div>
                      </div>
                      <div className="summary-questions-participant-color st-blue"/>
                    </div>
                ))}
                <div className="summary-questions-participant-heading">
                  <div className="summary-questions-participant-name">
                    <div className="name-rotate">Team Mean</div>
                  </div>
                  <div className="summary-questions-participant-color fill-grey"/>
                </div>
                <div className="summary-questions-participant-heading">
                  <div className="summary-questions-participant-name">
                    <div className="name-rotate">Team Variance</div>
                  </div>
                  <div className="summary-questions-participant-color fill-grey"/>
                </div>

                <div className="summary-questions-participant-heading function">
                  <div className="summary-questions-participant-name">
                    <div className="name-rotate">Function</div>
                  </div>
                  <div className="summary-questions-participant-color fill-grey"/>
                </div>
              </div>
              <div className="summary-questions-heading-end-spacer"/>
            </div>

            <div className="summary-questions-themes-container">
              {(questionsData ?? (surveyData?.pillars?.[cardType]?.themes || [])).map((theme, t_idx) => {
                return (
                    <ThemeRowContainer
                        key={t_idx}
                        theme={theme}
                        classname={getContainerClassname()}
                        calculateMeanScore={calculateMeanScore}
                        getFunctionText={getFunctionText}
                        tmEnabled={tmEnabled}
                        tlEnabled={tlEnabled}
                        stEnabled={stEnabled} //disabled stakeholders as scores dont align
                        t_idx={t_idx}
                        surveyData={surveyData}
                        cardType={cardType}
                        getContainerClassname={getContainerClassname}
                        teamMembers={teamMembers}
                        teamLeaders={teamLeaders}
                        stakeholders={stakeholders} //disabled stakeholders as scores dont align
                        generatingReport={generatingReport}
                        teamVariance={teamVariance[t_idx]}
                        teamMean={teamMean[t_idx]}
                        origin={origin}
                    />
                );
              })}

            </div>
          </div>
        </div>
        {showSubscriptionPopup && (
            <SubscriptionNeededPopup
                onClose={() => setShowSubscriptionPopup(false)}
            />
        )}
      </>
  );
};

export default SummaryQuestions;

